import React from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { cn } from "@helpers/utils";
import BaseTooltip from "@reusables/BaseTooltip";
import { StockCount } from "@/types/general";

/**
 * Names the store(s) a stock count covers.
 *
 * A merged count can span a dozen stores, and joining them all stretches the main table's Location
 * column far past its share of the width. Only the first store is spelled out; the rest collapse
 * into a hoverable "+N more", which keeps the cell to a predictable size without losing anything.
 */
export default function StockCountLocations({
    entries,
    className,
}: {
    entries: StockCount.Components.Entry[];
    className?: string;
}) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main.locations" });

    // Store-level only — sections would multiply the list without helping identify the count.
    const stores = _.uniq(entries.map((entry) => entry.store.name));
    const [first, ...rest] = stores;

    if (!first) return <span className={className}>{t("unknown")}</span>;

    return (
        <span className={className}>
            {first}
            {!!rest.length && (
                <>
                    {" "}
                    <BaseTooltip title={rest.join(", ")} placement="top">
                        {/* Dotted underline marks it as hoverable — nothing else signals it */}
                        <span className="whitespace-nowrap text-[#7556FA] underline decoration-dotted underline-offset-2">
                            {t("overflow", { amount: rest.length })}
                        </span>
                    </BaseTooltip>
                </>
            )}
        </span>
    );
}
